/**
 * Messaging service — conversations + messages CRUD.
 *
 * Singleton pattern. Throws ServiceError on failure.
 * Unread counts computed from message_reads table.
 */
import { supabase } from '../supabase'
import type { Cols, EmbedOne, WithEmbed } from '../types/db-rows'
import { ServiceError } from '../errors/service-error'
import { captureError } from '../utils/observability'
import { captureException } from '../utils/sentry'
import { signedUrls } from '../utils/storage-url'
import { currentUserIdOrThrow } from './session-identity'

/** Private bucket holding chat image attachments. */
const CHAT_MEDIA_BUCKET = 'chat-media'
import type {
  ConversationListItem,
  MessageItem,
  MessagePage,
  MessageReplyPreview,
  MessageSearchResult,
  SendMessageInput,
} from '../types/messaging.types'

// ── Row interfaces ───────────────────────────────────────────────────────────

type ConversationRow = Cols<
  'conversations',
  | 'id'
  | 'participant_ids'
  | 'rental_id'
  | 'last_message_preview'
  | 'last_message_at'
  | 'is_archived'
  | 'created_at'
>

// PostgREST rejects select strings containing literal newlines, so keep this
// single-line. Both message readers share it, and MessageRow below is the
// typed shape it returns.
const MESSAGE_SELECT =
  'id,conversation_id,sender_id,content,message_type,media_url,media_blurhash,location_lat,location_lng,is_edited,is_deleted,created_at,client_message_id,detected_types,reply_to_id,profiles!messages_sender_id_fkey(display_name,avatar_url)'

/** Shape returned by `MESSAGE_SELECT`. */
type MessageRow = WithEmbed<
  Cols<
    'messages',
    | 'id'
    | 'conversation_id'
    | 'sender_id'
    | 'content'
    | 'message_type'
    | 'media_url'
    | 'media_blurhash'
    | 'location_lat'
    | 'location_lng'
    | 'is_edited'
    | 'is_deleted'
    | 'created_at'
    | 'client_message_id'
    | 'detected_types'
    | 'reply_to_id'
  >,
  { profiles: EmbedOne<'profiles', 'display_name' | 'avatar_url'> }
>

type ReplyRow = Cols<'messages', 'id' | 'sender_id' | 'content' | 'message_type' | 'is_deleted'>

type MessageReadRow = Cols<'message_reads', 'conversation_id' | 'last_read_at'>

function messageReadId(conversationId: string, userId: string): string {
  return `read:${conversationId}:${userId}`
}

// ── Mappers ──────────────────────────────────────────────────────────────────

function mapReplyPreview(row: ReplyRow): MessageReplyPreview {
  return {
    id: row.id,
    senderId: row.sender_id,
    content: row.content,
    messageType: row.message_type,
    isDeleted: row.is_deleted,
  }
}

function mapMessage(
  row: MessageRow,
  replyPreviews: ReadonlyMap<string, MessageReplyPreview> = new Map(),
): MessageItem {
  return {
    id: row.id,
    conversationId: row.conversation_id,
    senderId: row.sender_id,
    senderDisplayName: row.profiles?.display_name ?? null,
    senderAvatarUrl: row.profiles?.avatar_url ?? null,
    content: row.content,
    messageType: row.message_type,
    mediaUrl: row.media_url,
    mediaBlurhash: row.media_blurhash,
    locationLat: row.location_lat,
    locationLng: row.location_lng,
    isEdited: row.is_edited,
    isDeleted: row.is_deleted,
    replyToId: row.reply_to_id,
    replyTo: row.reply_to_id ? (replyPreviews.get(row.reply_to_id) ?? null) : null,
    createdAt: row.created_at,
    clientMessageId: row.client_message_id,
    detectedTypes: row.detected_types ?? [],
  }
}

/**
 * Chat media lives in the private `chat-media` bucket. Rows hold a storage
 * path (or a legacy public URL from when the bucket was public) and readers
 * get a short-lived signed URL. Signing failures degrade to the raw value —
 * one broken thumbnail must not blank the whole thread.
 */
async function withSignedMedia(messages: MessageItem[]): Promise<MessageItem[]> {
  const media = messages.map((m) => m.mediaUrl).filter((u): u is string => !!u)
  if (media.length === 0) return messages

  let signed: string[]
  try {
    signed = await signedUrls(CHAT_MEDIA_BUCKET, media)
  } catch (err) {
    captureException(err, { scope: 'chat-media-signing' })
    return messages
  }

  const byOriginal = new Map(media.map((original, i) => [original, signed[i] ?? original]))
  return messages.map((m) =>
    m.mediaUrl ? { ...m, mediaUrl: byOriginal.get(m.mediaUrl) ?? m.mediaUrl } : m,
  )
}

// ── Service ──────────────────────────────────────────────────────────────────

class MessagingServiceImpl {
  /**
   * Batch-hydrate reply previews. Keeping this separate from MESSAGE_SELECT
   * avoids a recursive PostgREST embed and guarantees one extra query per
   * page, never one per bubble.
   */
  private async getReplyPreviews(
    replyIds: readonly (string | null | undefined)[],
  ): Promise<Map<string, MessageReplyPreview>> {
    const uniqueIds = [...new Set(replyIds.filter((id): id is string => Boolean(id)))]
    if (uniqueIds.length === 0) return new Map()

    const { data, error } = await supabase
      .from('messages')
      .select('id,sender_id,content,message_type,is_deleted')
      .in('id', uniqueIds)

    if (error) throw ServiceError.fromSupabaseError(error)
    return new Map((data ?? []).map((row) => [row.id, mapReplyPreview(row)]))
  }

  /** Fetch one reply preview for a realtime message whose parent is off-screen. */
  async getReplyPreview(replyId: string): Promise<MessageReplyPreview | null> {
    const previews = await this.getReplyPreviews([replyId])
    return previews.get(replyId) ?? null
  }

  /**
   * List conversations for a user with unread counts + other participant info.
   */
  async getConversations(userId: string): Promise<ConversationListItem[]> {
    // 1) Fetch conversations where user is a participant
    const { data: conversations, error: convError } = await supabase
      .from('conversations')
      .select(
        'id, participant_ids, rental_id, last_message_preview, last_message_at, is_archived, created_at',
      )
      .contains('participant_ids', [userId])
      .order('last_message_at', { ascending: false, nullsFirst: false })

    if (convError) throw ServiceError.fromSupabaseError(convError)
    if (!conversations || conversations.length === 0) return []

    const convRows: ConversationRow[] = conversations

    // 2) Fetch message_reads for this user (batch)
    const convIds = convRows.map((c) => c.id)
    const { data: reads } = await supabase
      .from('message_reads')
      .select('conversation_id, last_read_at')
      .eq('profile_id', userId)
      .in('conversation_id', convIds)

    const readMap = new Map<string, string>()
    const readRows: MessageReadRow[] = reads ?? []
    for (const r of readRows) {
      readMap.set(r.conversation_id, r.last_read_at)
    }

    // 3) Compute unread counts per conversation (batched, not N+1)
    const unreadCounts = new Map<string, number>()
    const needsCount = convRows.filter((conv) => {
      const lastRead = readMap.get(conv.id)
      if (!lastRead) return true
      return !!(conv.last_message_at && conv.last_message_at > lastRead)
    })

    // Parallel count queries for conversations with unread messages
    if (needsCount.length > 0) {
      const countResults = await Promise.all(
        needsCount.map((conv) => {
          const lastRead = readMap.get(conv.id)
          let query = supabase
            .from('messages')
            .select('*', { count: 'exact', head: true })
            .eq('conversation_id', conv.id)
            .neq('sender_id', userId)
            .eq('is_deleted', false)
          if (lastRead) query = query.gt('created_at', lastRead)
          return query.then((r) => ({ id: conv.id, count: r.count ?? 0 }))
        }),
      )
      for (const r of countResults) unreadCounts.set(r.id, r.count)
    }
    // Conversations with no unread stay at 0
    for (const conv of convRows) {
      if (!unreadCounts.has(conv.id)) unreadCounts.set(conv.id, 0)
    }

    // 4) Fetch other participants' profiles (batch)
    const otherUserIds = convRows.map(
      (c) => c.participant_ids.find((pid) => pid !== userId) ?? c.participant_ids[0]!,
    )
    const uniqueOtherIds = [...new Set(otherUserIds)]
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, display_name, avatar_url')
      .in('id', uniqueOtherIds)

    const profileMap = new Map<string, { display_name: string | null; avatar_url: string | null }>()
    for (const p of profiles ?? []) {
      profileMap.set(p.id, p)
    }

    // 5) Assemble + deduplicate by other participant
    // (Database may have duplicate conversations from before the sorted-ids fix)
    const assembled = convRows.map((conv) => {
      const otherUserId =
        conv.participant_ids.find((pid) => pid !== userId) ?? conv.participant_ids[0]!
      const otherProfile = profileMap.get(otherUserId)

      return {
        id: conv.id,
        participantIds: conv.participant_ids,
        otherUserId,
        otherUserDisplayName: otherProfile?.display_name ?? null,
        otherUserAvatarUrl: otherProfile?.avatar_url ?? null,
        rentalId: conv.rental_id,
        lastMessagePreview: conv.last_message_preview,
        lastMessageAt: conv.last_message_at,
        unreadCount: unreadCounts.get(conv.id) ?? 0,
        isArchived: conv.is_archived,
        createdAt: conv.created_at,
      }
    })

    // Keep only the most recent conversation per unique participant pair
    const seen = new Map<string, (typeof assembled)[number]>()
    for (const conv of assembled) {
      const existing = seen.get(conv.otherUserId)
      if (!existing || (conv.lastMessageAt ?? '') > (existing.lastMessageAt ?? '')) {
        seen.set(conv.otherUserId, conv)
      }
    }
    return Array.from(seen.values())
  }

  /**
   * Cursor-based paginated messages for a conversation (newest first).
   */
  async getMessages(conversationId: string, cursor?: string, limit = 30): Promise<MessagePage> {
    let query = supabase
      .from('messages')
      .select(MESSAGE_SELECT)
      .eq('conversation_id', conversationId)
      .eq('is_deleted', false)
      .order('created_at', { ascending: false })
      .limit(limit + 1)

    if (cursor) {
      query = query.lt('created_at', cursor)
    }

    const { data, error } = await query

    if (error) throw ServiceError.fromSupabaseError(error)

    const rows: MessageRow[] = data ?? []
    const hasMore = rows.length > limit
    const pageRows = hasMore ? rows.slice(0, limit) : rows
    const nextCursor = hasMore ? (pageRows[pageRows.length - 1]?.created_at ?? null) : null

    const replyPreviews = await this.getReplyPreviews(pageRows.map((row) => row.reply_to_id))

    return {
      messages: await withSignedMedia(pageRows.map((row) => mapMessage(row, replyPreviews))),
      nextCursor,
    }
  }

  /**
   * Send a message and update conversation's last_message preview.
   */
  async sendMessage(userId: string, input: SendMessageInput): Promise<MessageItem> {
    // sender_id is RLS-checked against auth.uid(). This is a hot path, so use
    // the local-session resolver (no per-message network round-trip) — it still
    // returns the exact id the request's token carries, so a hook-cached user id
    // that has drifted can't insert a message under the wrong sender.
    const liveId = await currentUserIdOrThrow()
    void userId

    // The trigger detects from the original text, then stores only redacted
    // content. Keep detected_types: [] in the INSERT as a schema guard: a
    // database without this migration must reject the unknown column instead
    // of accepting unfiltered content. The server never trusts this array.
    const { data: message, error: msgError } = await supabase
      .from('messages')
      .insert({
        conversation_id: input.conversationId,
        sender_id: liveId,
        content: input.content ?? null,
        message_type: input.messageType ?? 'text',
        media_url: input.mediaUrl ?? null,
        media_blurhash: input.mediaBlurhash ?? null,
        location_lat: input.locationLat ?? null,
        location_lng: input.locationLng ?? null,
        reply_to_id: input.replyToId ?? null,
        client_message_id: input.clientMessageId ?? null,
        detected_types: [],
      })
      .select(MESSAGE_SELECT)
      .single()

    if (msgError) throw ServiceError.fromSupabaseError(msgError)

    // Update conversation denormalized fields
    const preview =
      message.content?.slice(0, 100) ??
      (input.messageType === 'location' ? '[location]' : input.mediaUrl ? '[image]' : '')
    const { error: updateError } = await supabase
      .from('conversations')
      .update({
        last_message_preview: preview,
        last_message_at: message.created_at,
      })
      .eq('id', input.conversationId)

    if (updateError) {
      // The database trigger already updates conversation metadata inside
      // the message insert transaction. This client-side update only keeps
      // image previews pretty, so it must never make a delivered message look
      // like it failed to send.
      captureError(
        ServiceError.fromSupabaseError(updateError),
        { screen: 'messages', action: 'update-conversation-preview' },
        { conversationId: input.conversationId },
      )
    }

    const replyPreviews = input.replyTo
      ? new Map([[input.replyTo.id, input.replyTo]])
      : await this.getReplyPreviews([message.reply_to_id])
    const mapped = mapMessage(message, replyPreviews)
    return (await withSignedMedia([mapped]))[0] ?? mapped
  }

  /**
   * Full-text search stays server-side so long conversations do not need to
   * download their entire history. The RPC enforces conversation membership
   * independently of the UI and searches only stored, filtered text.
   */
  async searchConversationMessages(
    conversationId: string,
    query: string,
    limit = 30,
  ): Promise<MessageSearchResult[]> {
    const normalized = query.trim()
    if (normalized.length < 2) return []

    const { data, error } = await supabase.rpc('search_conversation_messages', {
      p_conversation_id: conversationId,
      p_query: normalized,
      p_limit: limit,
    })

    if (error) throw ServiceError.fromSupabaseError(error)
    return (data ?? []).map((row) => ({
      id: row.id,
      senderId: row.sender_id,
      content: row.content,
      messageType: row.message_type,
      createdAt: row.created_at,
      rank: row.rank,
    }))
  }

  /**
   * Mark all messages in a conversation as read for a user.
   */
  async markAsRead(conversationId: string, userId: string): Promise<void> {
    // The read-marker row is keyed on profile_id = auth.uid(); resolve the live
    // session id (local, no network) so the update filter, the primary-key, and
    // the inserted profile_id all match the token the request actually sends.
    // The cached `userId` is kept only for error telemetry (it reveals drift).
    const liveId = await currentUserIdOrThrow()
    const lastReadAt = new Date().toISOString()
    const { data: existingRead, error: updateError } = await supabase
      .from('message_reads')
      .update({ last_read_at: lastReadAt })
      .eq('conversation_id', conversationId)
      .eq('profile_id', liveId)
      .select('id')
      .maybeSingle()

    if (updateError) {
      captureError(
        ServiceError.fromSupabaseError(updateError),
        { screen: 'messages', action: 'mark-as-read' },
        { conversationId, userId },
      )
      return
    }

    if (existingRead?.id) return

    const { error } = await supabase.from('message_reads').insert({
      id: messageReadId(conversationId, liveId),
      conversation_id: conversationId,
      profile_id: liveId,
      last_read_at: lastReadAt,
    })

    if (error) {
      captureError(
        ServiceError.fromSupabaseError(error),
        { screen: 'messages', action: 'mark-as-read' },
        { conversationId, userId },
      )
    }
  }

  /**
   * Read-state rows for a conversation: one per participant.
   *
   * Only the counterpart's `last_read_at` is consumed today (the tick on the
   * sender's side), but returning both keeps the method honest about what the
   * policy exposes. RLS gained
   * `message_reads_conversation_participants` in
   * `20260927150000_los_recibos_de_lectura_son_del_par.sql`; before it this
   * query returned exactly one row — the caller's own — which is why the
   * sender never had a receipt to render.
   */
  async getConversationReadReceipts(
    conversationId: string,
  ): Promise<{ profileId: string; lastReadAt: string }[]> {
    const { data, error } = await supabase
      .from('message_reads')
      .select('profile_id, last_read_at')
      .eq('conversation_id', conversationId)

    if (error) throw ServiceError.fromSupabaseError(error)

    return (data ?? []).map((row) => ({
      profileId: row.profile_id as string,
      lastReadAt: row.last_read_at as string,
    }))
  }

  /**
   * Find existing conversation between two users or create a new one.
   */
  async getOrCreateConversation(
    userId: string,
    otherUserId: string,
    rentalId?: string,
  ): Promise<string> {
    if (userId === otherUserId) {
      throw ServiceError.validation('Cannot create conversation with yourself')
    }

    // Normalize participant order to prevent duplicates
    const sortedIds = [userId, otherUserId].sort()

    // Check if conversation already exists (both orderings)
    const { data: existing } = await supabase
      .from('conversations')
      .select('id')
      .contains('participant_ids', sortedIds)
      .limit(1)
      .maybeSingle()

    if (existing) return existing.id

    // Create new conversation with sorted participant order
    const { data: created, error } = await supabase
      .from('conversations')
      .insert({
        participant_ids: sortedIds,
        rental_id: rentalId ?? null,
      })
      .select('id')
      .single()

    if (error) {
      // Handle race condition: if another request created it simultaneously
      if (error.code === '23505') {
        const { data: retry } = await supabase
          .from('conversations')
          .select('id')
          .contains('participant_ids', sortedIds)
          .limit(1)
          .maybeSingle()
        if (retry) return retry.id
      }
      throw ServiceError.fromSupabaseError(error)
    }
    return created.id
  }

  /**
   * E.164 of the other participant, for the native dialer only.
   *
   * NULL when there is no paid rental, no confirmed phone, or a block.
   * Callers must not render this string.
   */
  async conversationCallPhone(conversationId: string): Promise<string | null> {
    const { data, error } = await supabase.rpc('conversation_call_phone', {
      p_conversation_id: conversationId,
    })
    if (error) throw ServiceError.fromSupabaseError(error)
    return conversationCallHref(data) ? data : null
  }

  /**
   * Total unread count across all conversations for tab badge.
   */
  async getUnreadTotal(userId: string): Promise<number> {
    const conversations = await this.getConversations(userId)
    return conversations.reduce((sum, c) => sum + c.unreadCount, 0)
  }
}

const CALL_PHONE = /^\+[1-9]\d{7,14}$/

/** `tel:` for a confirmed E.164. Anything else is not a phone call. */
export function conversationCallHref(phone: string | null | undefined): string | null {
  if (!phone || !CALL_PHONE.test(phone)) return null
  return `tel:${phone}`
}

export const messagingService = new MessagingServiceImpl()
